// A sent envelope: who has signed, actions (remind, void, correct an email, sign my part, download), the document
// with what's been filled in so far, and the audit trail. Admins see the same minus the document.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Ban, BellRing, CheckCircle2, Circle, Download, FileDown, FileText, Mail, PenLine, ShieldCheck } from "lucide-react";
import { api, startDownload, saveText } from "../lib/api";
import { EVENT_LABEL, RECIPIENT_STATUS, fullDate, rColor, shortDate, when } from "../lib/format";
import type { EnvelopeView, Field, Recipient } from "../lib/types";
import { Card, Chip, StatusChip, inputCls } from "../components/bits";
import { PdfPages } from "../components/PdfPages";
import { DeliveriesCard } from "../components/Connectors";
import { Button, Modal, PromptDialog, cx, toast } from "../components/ui";

export default function EnvelopeStatus({ view, admin, reload }: { view: EnvelopeView; admin?: boolean; reload: () => Promise<void> }) {
  const { envelope: env, recipients, events } = view;
  const nav = useNavigate();
  const [voiding, setVoiding] = useState(false);
  const [correcting, setCorrecting] = useState<Recipient | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const base = admin ? `/admin/envelopes/${env.id}` : `/envelopes/${env.id}`; // API paths
  const byRid = new Map(recipients.map((r) => [r.rid, r]));
  const signers = recipients.filter((r) => r.role === "signer");
  const ccs = recipients.filter((r) => r.role === "cc");
  const open = env.status === "sent";

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const download = (which: "final" | "original") => run(which, async () => startDownload((await api<{ url: string }>(`${base}/download?which=${which}`)).url));
  const audit = () => run("audit", async () => {
    const r = await api<{ fileName: string; csv: string }>(`${base}/audit`);
    saveText(r.fileName, r.csv);
  });
  const remind = (rid?: string) =>
    run(`remind:${rid ?? "all"}`, async () => {
      const r = await api<{ reminded: string[] }>(`/envelopes/${env.id}/remind`, { body: { rid } });
      toast(`Reminder sent to ${r.reminded.join(", ")}`);
      await reload();
    });

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <Link to={admin ? "/company/envelopes" : "/"} className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink mb-3">
        <ArrowLeft className="size-4" /> {admin ? "All envelopes" : "Envelopes"}
      </Link>
      <div className="flex flex-col md:flex-row md:items-start gap-3 mb-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold break-words">{env.title}</h1>
            <StatusChip status={env.status} />
          </div>
          <p className="text-sm text-muted mt-1">
            {admin && <>From {env.ownerName} ({env.ownerEmail}) · </>}
            Sent {fullDate(env.sentAt)}
            {open && env.expiresAt && <> · Expires {shortDate(env.expiresAt)}</>}
            {env.completedAt && env.status === "completed" && <> · Completed {fullDate(env.completedAt)}</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {view.canSignNow && !admin && (
            <Button variant="primary" onClick={() => nav(`/envelopes/${env.id}/sign`)}>
              <PenLine className="size-4" /> Sign now
            </Button>
          )}
          {env.status === "completed" && (!admin || view.docs) && (
            <Button variant="primary" busy={busy === "final"} onClick={() => download("final")}>
              <Download className="size-4" /> Signed PDF
            </Button>
          )}
          {open && !admin && (
            <Button busy={busy === "remind:all"} onClick={() => remind()}>
              <BellRing className="size-4" /> Remind
            </Button>
          )}
          {open && (
            <Button onClick={() => setVoiding(true)}>
              <Ban className="size-4" /> Void
            </Button>
          )}
          <Button busy={busy === "audit"} onClick={audit}>
            <FileDown className="size-4" /> Audit CSV
          </Button>
        </div>
      </div>

      {env.status === "declined" && (
        <Banner tone="bad" icon={<AlertTriangle className="size-4" />}>
          {env.declinedBy} declined to sign: “{env.declineReason}”
        </Banner>
      )}
      {env.status === "voided" && (
        <Banner tone="warn" icon={<Ban className="size-4" />}>
          Voided: “{env.voidReason}”
        </Banner>
      )}
      {env.status === "expired" && (
        <Banner tone="warn" icon={<AlertTriangle className="size-4" />}>
          Expired before everyone signed. Create a new envelope to try again.
        </Banner>
      )}
      {env.status === "completing" && (
        <Banner tone="accent" icon={<ShieldCheck className="size-4" />}>
          Everyone has signed. eSignIt is sealing the final PDF and emailing copies; this takes a few seconds.{" "}
          <button className="underline" onClick={() => void reload()}>
            Refresh
          </button>
        </Banner>
      )}
      {env.status === "completed" && (
        <Banner tone="good" icon={<ShieldCheck className="size-4" />}>
          Completed and sealed. Everyone was emailed the signed PDF{env.finalSize && env.finalSize > 10 * 1024 * 1024 ? " (as a download link, because it's large)" : ""}. SHA-256 <span className="font-mono text-xs break-all">{env.finalHash}</span>
        </Banner>
      )}
      {env.docsPurgedAt && <Banner tone="muted">Documents were deleted on {shortDate(env.docsPurgedAt)}, 90 days after this envelope stopped. The audit trail is kept.</Banner>}

      {(env.link || view.deliveries.length > 0) && (
        <div className="mb-5">
          <DeliveriesCard envelopeId={env.id} status={env.status} link={env.link} deliveries={view.deliveries} admin={admin} reload={reload} />
        </div>
      )}

      <div className="grid lg:grid-cols-[minmax(0,1fr)_22rem] gap-5">
        <div className="flex flex-col gap-5 min-w-0">
          <Card title={env.routing === "sequential" ? "Signers, in order" : "Signers"}>
            <ul className="divide-y divide-line">
              {signers.map((r) => (
                <li key={r.rid} className="py-3 flex items-start gap-3">
                  {r.status === "signed" ? <CheckCircle2 className="size-5 text-good shrink-0 mt-0.5" /> : r.status === "declined" ? <Ban className="size-5 text-bad shrink-0 mt-0.5" /> : <Circle className="size-5 shrink-0 mt-0.5" style={{ color: rColor(r.color) }} />}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">
                      {env.routing === "sequential" && <span className="text-muted tabular mr-1">{r.order}.</span>}
                      {r.name} {r.isSender && <Chip tone="accent">You</Chip>}
                    </div>
                    <div className="text-xs text-muted break-all">{r.email}</div>
                    <div className="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      <span className={cx(r.status === "signed" ? "text-good" : r.status === "declined" ? "text-bad" : "text-ink-2")}>{RECIPIENT_STATUS[r.status]}</span>
                      {r.signedAt && <span className="text-muted">{fullDate(r.signedAt)}</span>}
                      {!r.signedAt && r.viewedAt && <span className="text-muted">viewed {when(r.viewedAt)}</span>}
                      {!r.viewedAt && r.notifiedAt && <span className="text-muted">emailed {when(r.notifiedAt)}</span>}
                      {open && r.order > (env.currentOrder ?? 1) && r.status === "pending" && <span className="text-muted">waits for step {r.order - 1}</span>}
                    </div>
                    {r.bounced && (
                      <div className="text-xs text-bad mt-1 flex items-center gap-1">
                        <AlertTriangle className="size-3.5" /> Email bounced. Correct the address.
                      </div>
                    )}
                  </div>
                  {open && !admin && !r.isSender && !["signed", "declined"].includes(r.status) && (
                    <div className="flex gap-1 shrink-0">
                      {r.order === env.currentOrder && (
                        <Button variant="ghost" className="h-8 px-2" busy={busy === `remind:${r.rid}`} onClick={() => remind(r.rid)} title="Send a reminder">
                          <BellRing className="size-4" />
                        </Button>
                      )}
                      <Button variant="ghost" className="h-8 px-2" onClick={() => setCorrecting(r)} title="Correct email">
                        <Mail className="size-4" />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {ccs.length > 0 && <p className="text-xs text-muted pt-2 border-t border-line">Copies to {ccs.map((r) => `${r.name} (${r.email})`).join(", ")} when everyone has signed.</p>}
          </Card>

          {view.docUrl && env.pages && (
            <Card
              title={
                <span className="inline-flex items-center gap-2">
                  <FileText className="size-4" /> {env.status === "completed" ? "Document as signed" : "Document"}
                </span>
              }
              actions={
                <Button variant="ghost" className="h-8" busy={busy === "original"} onClick={() => download("original")}>
                  <Download className="size-4" /> Original
                </Button>
              }
            >
              <div className="bg-surface-2 rounded-lg p-3 max-h-[75vh] overflow-y-auto">
                <PdfPages url={view.docUrl} pages={env.pages} maxWidth={760} overlay={(p) => view.fields.filter((f) => f.page === p).map((f) => <Filled key={f.fid} f={f} r={byRid.get(f.rid)} images={view.images} />)} />
              </div>
            </Card>
          )}
        </div>

        <Card title="Activity">
          <ol className="relative border-l border-line ml-1.5 flex flex-col gap-3">
            {[...events].reverse().map((e, i) => {
              const r = e.rid ? byRid.get(e.rid) : undefined;
              return (
                <li key={i} className="pl-4 relative">
                  <span className="absolute -left-[5px] top-1.5 size-2.5 rounded-full border-2 border-surface" style={{ background: r ? rColor(r.color) : "var(--muted)" }} />
                  <div className="text-sm text-ink">
                    {EVENT_LABEL[e.type] ?? e.type}
                    {e.detail?.name ? `: ${e.detail.name}` : ""}
                  </div>
                  <div className="text-xs text-muted">
                    {r ? r.name : e.actor === "system" ? "eSignIt" : e.actor} · {fullDate(e.ts)}
                    {e.ip && <> · {e.ip}</>}
                  </div>
                  {(e.detail?.reason || e.detail?.message) && <div className="text-xs text-ink-2 mt-0.5">“{String(e.detail.reason ?? e.detail.message)}”</div>}
                </li>
              );
            })}
          </ol>
        </Card>
      </div>

      {voiding && (
        <PromptDialog
          title="Void this envelope?"
          label="Reason (signers will see it)"
          confirm="Void envelope"
          validate={(v) => (v.trim() ? null : "Give a reason")}
          onClose={() => setVoiding(false)}
          onSubmit={async (reason) => {
            await api(admin ? `/admin/envelopes/${env.id}/void` : `/envelopes/${env.id}/void`, { body: { reason } });
            toast("Voided. Anyone who had the link was told.");
            await reload();
          }}
        />
      )}
      {correcting && <CorrectDialog r={correcting} envId={env.id} onClose={() => setCorrecting(null)} onDone={reload} />}
    </div>
  );
}

function Banner({ tone, icon, children }: { tone: "good" | "bad" | "warn" | "accent" | "muted"; icon?: React.ReactNode; children: React.ReactNode }) {
  const cls = { good: "bg-good-soft text-good", bad: "bg-bad-soft text-bad", warn: "bg-warn-soft text-warn", accent: "bg-accent-soft text-accent-text", muted: "bg-surface-2 text-ink-2" }[tone];
  return (
    <div className={cx("flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm mb-4", cls)}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** A field as it was filled (or an outline if not yet). */
function Filled({ f, r, images }: { f: Field; r?: Recipient; images: Record<string, string> }) {
  const color = rColor(r?.color ?? 0);
  const style = { left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` };
  if (f.value === undefined)
    return <div className="absolute rounded-[3px] border-2 border-dashed" style={{ ...style, borderColor: color, background: `color-mix(in srgb, ${color} 8%, transparent)` }} title={`${r?.name ?? "Signer"}: not signed yet`} />;
  const img = (f.type === "signature" || f.type === "initials") && images[`${f.rid}:${f.type}`];
  return (
    <div className="absolute flex items-center text-black overflow-hidden" style={{ ...style, fontSize: "clamp(7px, 1.1vw, 12px)" }}>
      {img ? <img src={img} alt={`${r?.name ?? ""} ${f.type}`} className="max-h-full max-w-full object-contain" /> : f.type === "checkbox" ? f.value === "true" ? "✓" : "" : <span className="truncate px-0.5">{f.value}</span>}
    </div>
  );
}

function CorrectDialog({ r, envId, onClose, onDone }: { r: Recipient; envId: string; onClose: () => void; onDone: () => Promise<void> }) {
  const [name, setName] = useState(r.name);
  const [email, setEmail] = useState(r.email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Correct recipient"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api(`/envelopes/${envId}/recipients/${r.rid}`, { body: { name, email } });
                toast("Updated. Their old link no longer works.");
                await onDone();
                onClose();
              } catch (e) {
                setError((e as Error).message);
                setBusy(false);
              }
            }}
          >
            Save and resend
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted mb-3">The previous link stops working. If it's their turn, the new address gets a fresh email.</p>
      <label className="block text-sm font-medium text-ink-2 mb-1">Name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
      <label className="block text-sm font-medium text-ink-2 mb-1 mt-3">Email</label>
      <input value={email} type="email" onChange={(e) => setEmail(e.target.value.trim())} className={inputCls} />
      {error && <p className="text-sm text-bad mt-2">{error}</p>}
    </Modal>
  );
}
